import { useEffect, useMemo, useState } from "react";
import { Mail, Eye, Save, RefreshCw, Send, Code2, MousePointerClick } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";

const TEMPLATE_META = [
  { key: "payment_confirmation", label: "Betalingsbevestiging", desc: "Verstuurd direct na een succesvolle betaling." },
  { key: "report_ready", label: "Rapport klaar", desc: "Wanneer de analyse afgerond is." },
  { key: "deletion_reminder", label: "Verwijdering herinnering", desc: "3 dagen voor automatische verwijdering." },
  { key: "invoice", label: "Factuur", desc: "Verstuurd met PDF-bijlage na betaling." },
  { key: "bundle_purchased", label: "3-pack credits", desc: "Verstuurd na aankoop van een 3-pack." },
  { key: "referral_invite", label: "Refer-a-Friend uitnodiging", desc: "24u na het rapport — bevat unieke kortingscode + social-share links." },
];

const PLACEHOLDERS = [
  ["{{order_id}}", "Order-referentie"],
  ["{{first_name}}", "Voornaam klant"],
  ["{{type_offerte}}", "Type offerte"],
  ["{{score}}", "Score (0-100)"],
  ["{{days_left}}", "Dagen tot verwijdering"],
  ["{{amount}}", "Bedrag (€)"],
  ["{{site_url}}", "Website URL"],
  ["{{report_url}}", "Directe link naar rapport"],
  ["{{discount_code}}", "Unieke kortingscode (refer-a-friend)"],
  ["{{percent_off}}", "Kortingspercentage (bv. 20)"],
  ["{{valid_until}}", "Vervaldatum kortingscode (YYYY-MM-DD)"],
  ["{{email}}", "E-mail van de ontvanger"],
];

export default function AdminEmails() {
  const [templates, setTemplates] = useState(null);
  const [activeKey, setActiveKey] = useState("payment_confirmation");
  const [editor, setEditor] = useState({ subject: "", html: "" });
  const [previewHtml, setPreviewHtml] = useState("");
  const [mode, setMode] = useState("visual"); // visual | html
  const [testEmail, setTestEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/email-templates");
    setTemplates(data);
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!templates) return;
    const t = templates[activeKey];
    setEditor({ subject: t.subject, html: t.html });
    setPreviewHtml("");
  }, [activeKey, templates]);

  const meta = useMemo(() => TEMPLATE_META.find((m) => m.key === activeKey), [activeKey]);

  const preview = async () => {
    const { data } = await api.post(`/admin/email-templates/${activeKey}/preview`, editor);
    setPreviewHtml(data.html);
  };

  const save = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/email-templates/${activeKey}`, editor);
      toast.success("Template opgeslagen");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setLoading(false);
    }
  };

  const reset = async () => {
    if (!confirm("Terugzetten naar default?")) return;
    await api.post(`/admin/email-templates/${activeKey}/reset`);
    toast.success("Hersteld naar default");
    load();
  };

  const sendTest = async () => {
    if (!testEmail) return;
    try {
      const { data } = await api.post(`/admin/email-templates/${activeKey}/test-send`, { to: testEmail });
      if (data.sent) toast.success("Test-e-mail verstuurd naar " + testEmail);
      else toast.error("SMTP niet bereikbaar — controleer .env");
    } catch {
      toast.error("Versturen mislukt");
    }
  };

  if (!templates) return <p>Laden...</p>;

  return (
    <div className="space-y-7">
      <div>
        <h1 className="font-display font-bold text-oc-primary text-[34px] flex items-center gap-3">
          <Mail className="w-7 h-7 text-oc-secondary" /> E-mail templates
        </h1>
        <p className="text-oc-textSecondary mt-1">Bewerk alle transactionele e-mails. Onderwerp + HTML-inhoud volledig aanpasbaar.</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-5">
        {/* Template list */}
        <div className="bg-white border border-oc-border rounded-oc p-2 lg:col-span-1 h-fit">
          {TEMPLATE_META.map((m) => (
            <button
              key={m.key}
              data-testid={`tpl-${m.key}`}
              onClick={() => setActiveKey(m.key)}
              className={`w-full text-left px-4 py-3 rounded-oc-btn transition-colors ${
                activeKey === m.key ? "bg-oc-primary text-white" : "hover:bg-oc-subtle"
              }`}
            >
              <p className={`font-display font-semibold text-[14px] ${activeKey === m.key ? "text-white" : "text-oc-primary"}`}>{m.label}</p>
              <p className={`text-[12px] mt-0.5 ${activeKey === m.key ? "text-white/75" : "text-oc-textSecondary"}`}>{m.desc}</p>
            </button>
          ))}
        </div>

        {/* Editor + preview */}
        <div className="lg:col-span-3 space-y-5">
          <div className="bg-white border border-oc-border rounded-oc p-7 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-display font-semibold text-oc-primary text-[20px]">{meta?.label}</h2>
                <p className="text-oc-textSecondary text-[13px]">{meta?.desc}</p>
              </div>
              <div className="flex gap-1 p-1 bg-oc-subtle border border-oc-border rounded-oc-btn">
                <button
                  onClick={() => setMode("visual")}
                  className={`px-3 py-1.5 rounded-md text-[13px] font-semibold flex items-center gap-2 ${mode === "visual" ? "bg-oc-primary text-white" : "text-oc-textSecondary"}`}
                >
                  <MousePointerClick className="w-3.5 h-3.5" /> Visueel
                </button>
                <button
                  onClick={() => setMode("html")}
                  className={`px-3 py-1.5 rounded-md text-[13px] font-semibold flex items-center gap-2 ${mode === "html" ? "bg-oc-primary text-white" : "text-oc-textSecondary"}`}
                >
                  <Code2 className="w-3.5 h-3.5" /> HTML
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Onderwerp</Label>
              <Input
                data-testid="tpl-subject"
                value={editor.subject}
                onChange={(e) => setEditor({ ...editor, subject: e.target.value })}
                className="h-12 rounded-oc-btn"
              />
            </div>

            <div className="space-y-2">
              <Label>Inhoud</Label>
              {mode === "visual" ? (
                <RichTextEditor value={editor.html} onChange={(html) => setEditor({ ...editor, html })} minHeight="280px" />
              ) : (
                <Textarea
                  data-testid="tpl-html"
                  value={editor.html}
                  onChange={(e) => setEditor({ ...editor, html: e.target.value })}
                  className="min-h-[300px] font-mono text-[13px] rounded-oc-btn"
                />
              )}
            </div>

            <div className="rounded-oc-btn bg-oc-subtle border border-oc-border p-4">
              <p className="text-[12px] uppercase tracking-[0.05em] font-semibold text-oc-textSecondary mb-2">Beschikbare placeholders</p>
              <div className="flex flex-wrap gap-2">
                {PLACEHOLDERS.map(([token, desc]) => (
                  <button
                    key={token}
                    onClick={() => navigator.clipboard?.writeText(token)}
                    title={`${desc} — klik om te kopiëren`}
                    className="px-2.5 py-1 rounded-md bg-white border border-oc-border font-mono text-[12px] hover:border-oc-secondary"
                  >
                    {token}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button data-testid="tpl-save" onClick={save} disabled={loading} className="bg-oc-primary hover:bg-oc-secondary text-white rounded-oc-btn h-11 px-6 font-semibold">
                <Save className="w-4 h-4 mr-2" /> Opslaan
              </Button>
              <Button onClick={preview} variant="outline" className="rounded-oc-btn h-11 px-6 border-oc-border text-oc-primary hover:bg-oc-subtle font-semibold">
                <Eye className="w-4 h-4 mr-2" /> Voorvertoning
              </Button>
              <Button onClick={reset} variant="outline" className="rounded-oc-btn h-11 px-6 border-oc-border text-oc-textSecondary hover:bg-oc-subtle">
                <RefreshCw className="w-4 h-4 mr-2" /> Reset
              </Button>
              <div className="flex-1" />
              <div className="flex gap-2 items-center">
                <Input
                  data-testid="tpl-test-email"
                  placeholder="test@webtron.be"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="h-11 rounded-oc-btn w-56"
                />
                <Button onClick={sendTest} disabled={!testEmail} className="bg-oc-secondary hover:bg-oc-primary text-white rounded-oc-btn h-11 px-5 font-semibold">
                  <Send className="w-4 h-4 mr-2" /> Test versturen
                </Button>
              </div>
            </div>
          </div>

          {previewHtml && (
            <div className="bg-white border border-oc-border rounded-oc p-2">
              <iframe
                title="Email preview"
                srcDoc={previewHtml}
                className="w-full h-[640px] rounded-oc-btn border border-oc-border bg-white"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
